import type { ElementType, HTMLAttributes, ReactNode } from 'react';

// ─── Roles del design system ──────────────────────────────────────────────────
// Fuente: Figma "02. Tipografía". Cada rol es una tupla completa —tamaño,
// line-height y peso— declarada en `tailwind.config.js`. Al usarlos NO hace
// falta pasar `fontWeight`: el peso viene dentro del token.
// Valores y procedencia: .github/docs/design-system/FIGMA-TOKENS.md
//
// Las clases se escriben literales a propósito: Tailwind escanea el código
// fuente y no detecta nombres construidos por concatenación.

export const designSystemFontSizes = [
  'title-1',
  'title-2',
  'title-3',
  'subtitle-1',
  'subtitle-2',
  'body-1',
  'body-2',
  'button',
  'caption',
  'overline-1',
  'overline-2',
] as const;

export type DesignSystemFontSize = typeof designSystemFontSizes[number];

// ─── Variantes ────────────────────────────────────────────────────────────────

export const textVariants = {
  fontWeight: {
    thin: 'font-thin',
    extraLight: 'font-extralight',
    light: 'font-light',
    regular: 'font-normal',
    medium: 'font-medium',
    semiBold: 'font-semibold',
    bold: 'font-bold',
    extraBold: 'font-extrabold',
    black: 'font-black',
  },
  fontSize: {
    // Roles del design system — usar estos en código nuevo.
    'title-1': 'text-title-1',
    'title-2': 'text-title-2',
    'title-3': 'text-title-3',
    'subtitle-1': 'text-subtitle-1',
    'subtitle-2': 'text-subtitle-2',
    'body-1': 'text-body-1',
    'body-2': 'text-body-2',
    // `button` incluye tracking y el design system lo marca como Caps:
    // acompáñalo con `className="uppercase"`.
    button: 'text-button',
    caption: 'text-caption',
    'overline-1': 'text-overline-1',
    'overline-2': 'text-overline-2',

    // Escala genérica de Tailwind — LEGADO. No usar en código nuevo; existe
    // para que la migración sea aditiva y se retira al terminarla.
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
    '5xl': 'text-5xl',
    '6xl': 'text-6xl',
    '7xl': 'text-7xl',
    '8xl': 'text-8xl',
    '9xl': 'text-9xl',
  },
} as const;

// ─── Props ────────────────────────────────────────────────────────────────────

export type FontWeightKeys = keyof typeof textVariants.fontWeight;
export type FontSizeKeys = keyof typeof textVariants.fontSize;

/** Colores semánticos del design system (`color/text/*` en Figma). */
type DesignSystemTextColor =
  | 'text-fg-primary'
  | 'text-fg-secondary'
  | 'text-fg-tertiary'
  | 'text-fg-disabled'
  | 'text-fg-on-brand'
  | 'text-fg-brand'
  | 'text-fg-brand-hover'
  | 'text-fg-success'
  | 'text-fg-error'
  | 'text-fg-warning'
  | 'text-fg-cashea'
  | 'text-fg-neutral';

/**
 * `string & {}` conserva el autocompletado de los tokens pero sigue aceptando
 * valores computados: hay ~12 usos que pasan el color desde una variable o un
 * ternario (`textColor={isDanger ? 'text-fg-error' : 'text-fg-tertiary'}`).
 * Tiparlo como unión estricta obligaría a refactorizar esos casos.
 */
export type TextColor = DesignSystemTextColor | (string & {});

export interface TextProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  children: ReactNode;
  as?: ElementType;
  className?: string;
  textColor?: TextColor;
  /** Omítelo al usar un rol del design system: el peso ya viene en el token. */
  fontWeight?: FontWeightKeys;
  fontSize?: FontSizeKeys;
}
